import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SubscriptionManager, LogoUploader } from '@/components/admin/SubscriptionManager';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  TrendingUp,
  DollarSign,
  Search,
  Eye,
  Trash2,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  LogIn,
  Shield,
  Settings,
  Activity,
  CreditCard,
  Ban,
  Play,
  Key,
  MoreVertical,
  ChevronRight,
  ChevronLeft,
  Phone,
  FileText,
  LayoutDashboard,
  Bell,
  Mail,
  Database,
  Globe,
  Megaphone,
  Receipt,
  PieChart,
  BarChart3,
  UserPlus,
  AlertCircle,
  Download,
  Upload,
  Sliders,
  Lock,
  Layers,
  Package,
  Server,
  Zap,
  Crown,
  X,
  Plus,
  Edit,
  Send,
  BookOpen,
  Info
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PieChart as RePieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import { cn } from '@/lib/utils';

const COLORS = ['#22c55e', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6', '#f97316'];

const statusColors = {
  trialing: 'bg-blue-500/20 text-blue-400',
  active: 'bg-green-500/20 text-green-400',
  past_due: 'bg-yellow-500/20 text-yellow-400',
  canceled: 'bg-red-500/20 text-red-400',
  unpaid: 'bg-orange-500/20 text-orange-400'
};

// Sidebar Navigation Items
const sidebarItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'system-health', label: 'System Health', icon: Activity },
  { id: 'tenants', label: 'Tenants', icon: Building2 },
  { id: 'users', label: 'All Users', icon: Users },
  { id: 'feature-flags', label: 'Feature Flags', icon: Sliders },
  { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { id: 'billing', label: 'Billing & Revenue', icon: DollarSign },
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'plans', label: 'Plan Management', icon: Package },
  { id: 'audit', label: 'Audit Logs', icon: Activity },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'system', label: 'System Settings', icon: Settings },
  { id: 'documentation', label: 'Documentation', icon: BookOpen },
];

export default function SuperAdminDashboard() {
  const { getAuthHeaders, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetailOpen, setTenantDetailOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [tenantSessions, setTenantSessions] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantAuditLogs, setTenantAuditLogs] = useState([]);
  const [tenantBillingEvents, setTenantBillingEvents] = useState([]);
  const [editingLimits, setEditingLimits] = useState(null);
  const [editingBranding, setEditingBranding] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  
  // Dialog states
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [announcementText, setAnnouncementText] = useState('');
  const [createPlanDialog, setCreatePlanDialog] = useState(false);
  const [editPlanData, setEditPlanData] = useState(null);
  
  // System Health states
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  
  // Tenant Configuration states
  const [tenantConfig, setTenantConfig] = useState(null);
  const [editingFeatures, setEditingFeatures] = useState(null);
  const [savingFeatures, setSavingFeatures] = useState(false);
  
  // Feature Flags states
  const [featureFlags, setFeatureFlags] = useState([]);
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);
  const [createFlagDialog, setCreateFlagDialog] = useState(false);
  const [newFlag, setNewFlag] = useState({ name: '', key: '', description: '', default_enabled: false, rollout_percentage: 100 });
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [flagOverrides, setFlagOverrides] = useState([]);
  const [flagOverridesDialog, setFlagOverridesDialog] = useState(false);
  
  // Announcements states
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [createAnnouncementDialog, setCreateAnnouncementDialog] = useState(false);
  const [editAnnouncementDialog, setEditAnnouncementDialog] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '', message: '', type: 'info', target_type: 'all', target_ids: [], is_dismissible: true
  });
  
  // Enhanced Audit Logs states
  const [detailedAuditLogs, setDetailedAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsPagination, setAuditLogsPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [auditFilters, setAuditFilters] = useState({
    action_type: '', tenant_id: '', resource_type: '', search: '', start_date: '', end_date: ''
  });
  const [auditFilterOptions, setAuditFilterOptions] = useState({ action_types: [], resource_types: [] });
  const [auditStats, setAuditStats] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchTenants();
    fetchPlans();
  }, []);

  useEffect(() => {
    if (activeSection === 'users') fetchAllUsers();
    if (activeSection === 'api-keys') fetchAllApiKeys();
    if (activeSection === 'audit') {
      fetchDetailedAuditLogs();
      fetchAuditStats();
    }
    if (activeSection === 'system-health') {
      fetchSystemHealth();
      fetchSystemMetrics();
      fetchProcessingStatus();
    }
    if (activeSection === 'feature-flags') fetchFeatureFlags();
    if (activeSection === 'announcements') fetchAnnouncements();
  }, [activeSection]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/super-admin/dashboard/detailed', {
        headers: getAuthHeaders()
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      try {
        const response = await api.get('/super-admin/dashboard', { headers: getAuthHeaders() });
        setDashboardData(response.data);
      } catch (e) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/super-admin/tenants', { headers: getAuthHeaders(), params });
      setTenants(response.data.tenants);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/plans/');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      // Get users for each tenant
      const users = [];
      for (const tenant of tenants) {
        try {
          const res = await api.get(`/super-admin/tenants/${tenant.id}/users`, { headers: getAuthHeaders() });
          res.data.users.forEach(u => {
            users.push({ ...u, tenant_name: tenant.name, tenant_id: tenant.id });
          });
        } catch (e) {}
      }
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchAllApiKeys = async () => {
    try {
      const response = await api.get('/api-keys/admin/all', { headers: getAuthHeaders() });
      setApiKeys(response.data.api_keys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await api.get('/compliance/admin/audit-logs?limit=200', { headers: getAuthHeaders() });
      setAuditLogs(response.data.logs);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  // System Health Functions
  const fetchSystemHealth = async () => {
    setHealthLoading(true);
    try {
      const response = await api.get('/super-admin/system/health', { headers: getAuthHeaders() });
      setSystemHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      // Set a default error state
      setSystemHealth({
        status: 'unknown',
        components: {},
        alerts: [{ type: 'error', message: 'Failed to fetch system health' }]
      });
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchSystemMetrics = async (period = '24h') => {
    setMetricsLoading(true);
    try {
      const response = await api.get(`/super-admin/system/metrics?period=${period}`, { headers: getAuthHeaders() });
      setSystemMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch system metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchProcessingStatus = async () => {
    try {
      const response = await api.get('/super-admin/system/processing-status', { headers: getAuthHeaders() });
      setProcessingStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch processing status:', error);
    }
  };

  const fetchTenantFullConfig = async (tenantId) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}/full-config`, { headers: getAuthHeaders() });
      setTenantConfig(response.data);
      setEditingFeatures(response.data.features);
    } catch (error) {
      console.error('Failed to fetch tenant config:', error);
      toast.error('Failed to load tenant configuration');
    }
  };

  const updateTenantFeatures = async (tenantId, features) => {
    setSavingFeatures(true);
    try {
      await api.put(`/super-admin/tenants/${tenantId}/features`, features, { headers: getAuthHeaders() });
      toast.success('Features updated successfully');
      fetchTenantFullConfig(tenantId);
    } catch (error) {
      console.error('Failed to update features:', error);
      toast.error('Failed to update features');
    } finally {
      setSavingFeatures(false);
    }
  };

  // Feature Flags Functions
  const fetchFeatureFlags = async () => {
    setFeatureFlagsLoading(true);
    try {
      const response = await api.get('/super-admin/feature-flags', { headers: getAuthHeaders() });
      setFeatureFlags(response.data.flags || []);
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
    } finally {
      setFeatureFlagsLoading(false);
    }
  };

  const handleCreateFlag = async () => {
    if (!newFlag.name || !newFlag.key) {
      toast.error('Name and key are required');
      return;
    }
    try {
      await api.post('/super-admin/feature-flags', newFlag, { headers: getAuthHeaders() });
      toast.success('Feature flag created');
      setCreateFlagDialog(false);
      setNewFlag({ name: '', key: '', description: '', default_enabled: false, rollout_percentage: 100 });
      fetchFeatureFlags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create feature flag');
    }
  };

  const handleToggleFlag = async (flag) => {
    try {
      await api.put(`/super-admin/feature-flags/${flag.id}`, { default_enabled: !flag.default_enabled }, { headers: getAuthHeaders() });
      toast.success(`Flag ${!flag.default_enabled ? 'enabled' : 'disabled'}`);
      fetchFeatureFlags();
    } catch (error) {
      toast.error('Failed to toggle flag');
    }
  };

  const handleDeleteFlag = async (flagId) => {
    if (!confirm('Are you sure you want to delete this feature flag? This will also remove all tenant overrides.')) return;
    try {
      await api.delete(`/super-admin/feature-flags/${flagId}`, { headers: getAuthHeaders() });
      toast.success('Feature flag deleted');
      fetchFeatureFlags();
    } catch (error) {
      toast.error('Failed to delete flag');
    }
  };

  const fetchFlagOverrides = async (flag) => {
    setSelectedFlag(flag);
    try {
      const response = await api.get(`/super-admin/feature-flags/${flag.id}/overrides`, { headers: getAuthHeaders() });
      setFlagOverrides(response.data.overrides || []);
      setFlagOverridesDialog(true);
    } catch (error) {
      toast.error('Failed to fetch overrides');
    }
  };

  const handleSetOverride = async (tenantId, enabled) => {
    if (!selectedFlag) return;
    try {
      await api.put(`/super-admin/feature-flags/${selectedFlag.id}/overrides/${tenantId}?enabled=${enabled}`, {}, { headers: getAuthHeaders() });
      toast.success('Override set');
      fetchFlagOverrides(selectedFlag);
    } catch (error) {
      toast.error('Failed to set override');
    }
  };

  const handleRemoveOverride = async (tenantId) => {
    if (!selectedFlag) return;
    try {
      await api.delete(`/super-admin/feature-flags/${selectedFlag.id}/overrides/${tenantId}`, { headers: getAuthHeaders() });
      toast.success('Override removed');
      fetchFlagOverrides(selectedFlag);
    } catch (error) {
      toast.error('Failed to remove override');
    }
  };

  // User Impersonation
  const handleImpersonateUser = async (user) => {
    setImpersonating(true);
    try {
      const response = await api.post(`/super-admin/users/${user.id}/impersonate`, {}, { headers: getAuthHeaders() });
      const { access_token, tenant, user: impersonatedUser } = response.data;
      const url = `/dashboard?impersonate=${access_token}`;
      window.open(url, '_blank');
      toast.success(`Logged in as ${impersonatedUser.email} (${tenant.name})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    } finally {
      setImpersonating(false);
    }
  };

  // Announcements Functions
  const fetchAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const response = await api.get('/super-admin/announcements?include_inactive=true', { headers: getAuthHeaders() });
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error('Title and message are required');
      return;
    }
    try {
      await api.post('/super-admin/announcements', newAnnouncement, { headers: getAuthHeaders() });
      toast.success('Announcement created');
      setCreateAnnouncementDialog(false);
      setNewAnnouncement({ title: '', message: '', type: 'info', target_type: 'all', target_ids: [], is_dismissible: true });
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create announcement');
    }
  };

  const handleUpdateAnnouncement = async () => {
    if (!selectedAnnouncement) return;
    try {
      await api.put(`/super-admin/announcements/${selectedAnnouncement.id}`, selectedAnnouncement, { headers: getAuthHeaders() });
      toast.success('Announcement updated');
      setEditAnnouncementDialog(false);
      setSelectedAnnouncement(null);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await api.delete(`/super-admin/announcements/${id}`, { headers: getAuthHeaders() });
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleToggleAnnouncementActive = async (announcement) => {
    try {
      await api.put(`/super-admin/announcements/${announcement.id}`, { is_active: !announcement.is_active }, { headers: getAuthHeaders() });
      toast.success(`Announcement ${announcement.is_active ? 'deactivated' : 'activated'}`);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  // Enhanced Audit Logs Functions
  const fetchDetailedAuditLogs = async (page = 1) => {
    setAuditLogsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (auditFilters.action_type) params.append('action_type', auditFilters.action_type);
      if (auditFilters.tenant_id) params.append('tenant_id', auditFilters.tenant_id);
      if (auditFilters.resource_type) params.append('resource_type', auditFilters.resource_type);
      if (auditFilters.search) params.append('search', auditFilters.search);
      if (auditFilters.start_date) params.append('start_date', auditFilters.start_date);
      if (auditFilters.end_date) params.append('end_date', auditFilters.end_date);
      
      const response = await api.get(`/super-admin/audit-logs/detailed?${params}`, { headers: getAuthHeaders() });
      setDetailedAuditLogs(response.data.logs || []);
      setAuditLogsPagination({
        page: response.data.page,
        total: response.data.total,
        totalPages: response.data.total_pages
      });
      setAuditFilterOptions(response.data.filters || { action_types: [], resource_types: [] });
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setAuditLogsLoading(false);
    }
  };

  const fetchAuditStats = async () => {
    try {
      const response = await api.get('/super-admin/audit-logs/stats?days=7', { headers: getAuthHeaders() });
      setAuditStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit stats:', error);
    }
  };

  const handleExportAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilters.start_date) params.append('start_date', auditFilters.start_date);
      if (auditFilters.end_date) params.append('end_date', auditFilters.end_date);
      if (auditFilters.tenant_id) params.append('tenant_id', auditFilters.tenant_id);
      if (auditFilters.action_type) params.append('action_type', auditFilters.action_type);
      
      const response = await api.get(`/super-admin/audit-logs/export?${params}`, { headers: getAuthHeaders() });
      
      // Convert to CSV
      const data = response.data.data;
      if (!data.length) {
        toast.error('No data to export');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success(`Exported ${data.length} records`);
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  const fetchTenantDetails = async (tenantId) => {
    try {
      const [detailRes, sessionsRes, usersRes, auditRes, billingRes] = await Promise.all([
        api.get(`/super-admin/tenants/${tenantId}`, { headers: getAuthHeaders() }),
        api.get(`/super-admin/tenants/${tenantId}/sessions`, { headers: getAuthHeaders() }).catch(() => ({ data: { sessions: [] } })),
        api.get(`/super-admin/tenants/${tenantId}/users`, { headers: getAuthHeaders() }),
        api.get(`/super-admin/tenants/${tenantId}/audit-logs`, { headers: getAuthHeaders() }).catch(() => ({ data: { logs: [] } })),
        api.get(`/super-admin/tenants/${tenantId}/billing-events`, { headers: getAuthHeaders() }).catch(() => ({ data: { events: [] } }))
      ]);
      
      setSelectedTenant(detailRes.data);
      setTenantSessions(sessionsRes.data.sessions || []);
      setTenantUsers(usersRes.data.users || []);
      setTenantAuditLogs(auditRes.data.logs || []);
      setTenantBillingEvents(billingRes.data.events || []);
      setTenantDetailOpen(true);
    } catch (error) {
      console.error('Failed to fetch tenant details:', error);
      toast.error('Failed to load tenant details');
    }
  };

  const handleEnterTenant = async (tenantId) => {
    setImpersonating(true);
    try {
      const response = await api.post(`/super-admin/tenants/${tenantId}/enter`, {}, { headers: getAuthHeaders() });
      const { access_token, tenant, user } = response.data;
      const url = `/dashboard?impersonate=${access_token}`;
      window.open(url, '_blank');
      toast.success(`Entering ${tenant.name} as ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter tenant');
    } finally {
      setImpersonating(false);
    }
  };

  const handleSuspendTenant = async (tenantId, suspend = true) => {
    const action = suspend ? 'suspend' : 'activate';
    if (suspend && !confirm(`Are you sure you want to suspend this tenant? All users will be logged out.`)) return;
    
    try {
      await api.post(`/super-admin/tenants/${tenantId}/${action}`, {}, { headers: getAuthHeaders() });
      toast.success(`Tenant ${suspend ? 'suspended' : 'activated'}`);
      fetchTenants();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} tenant`);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!confirm('Are you sure you want to DELETE this tenant? This action CANNOT be undone.')) return;
    
    try {
      await api.delete(`/super-admin/tenants/${tenantId}`, { headers: getAuthHeaders() });
      toast.success('Tenant deleted');
      fetchTenants();
      fetchDashboardData();
      setTenantDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const handleUpdateLimits = async () => {
    if (!editingLimits || !selectedTenant) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/limits`, editingLimits, { headers: getAuthHeaders() });
      toast.success('Limits updated');
      fetchTenantDetails(selectedTenant.tenant.id);
      setEditingLimits(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update limits');
    }
  };

  const handleUpdateBranding = async () => {
    if (!editingBranding || !selectedTenant) return;
    try {
      await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/branding`, editingBranding, { headers: getAuthHeaders() });
      toast.success('Branding updated');
      fetchTenantDetails(selectedTenant.tenant.id);
      setEditingBranding(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branding');
    }
  };

  const handleTerminateSession = async (tenantId, sessionId) => {
    try {
      await api.post(`/super-admin/tenants/${tenantId}/sessions/${sessionId}/terminate`, {}, { headers: getAuthHeaders() });
      toast.success('Session terminated');
      fetchTenantDetails(tenantId);
    } catch (error) {
      toast.error('Failed to terminate session');
    }
  };

  const handleTerminateAllSessions = async (tenantId) => {
    if (!confirm('Terminate ALL sessions for this tenant?')) return;
    try {
      await api.post(`/super-admin/tenants/${tenantId}/sessions/terminate-all`, {}, { headers: getAuthHeaders() });
      toast.success('All sessions terminated');
      fetchTenantDetails(tenantId);
    } catch (error) {
      toast.error('Failed to terminate sessions');
    }
  };

  const handleResetPassword = async (tenantId, userId) => {
    const newPassword = prompt('Enter new password (min 6 characters):');
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/super-admin/tenants/${tenantId}/users/${userId}/reset-password`, { new_password: newPassword }, { headers: getAuthHeaders() });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!confirm('Revoke this API key?')) return;
    try {
      await api.post(`/api-keys/admin/revoke/${keyId}`, {}, { headers: getAuthHeaders() });
      toast.success('API key revoked');
      fetchAllApiKeys();
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  // Stats for clickable cards
  const stats = dashboardData?.stats || {};
  const subscriptionData = dashboardData?.subscription_breakdown 
    ? Object.entries(dashboardData.subscription_breakdown).map(([name, value]) => ({ name, value }))
    : [];
  const planData = dashboardData?.plan_breakdown
    ? (Array.isArray(dashboardData.plan_breakdown) 
        ? dashboardData.plan_breakdown.map(p => ({ name: p.name, value: p.count }))
        : Object.entries(dashboardData.plan_breakdown).map(([name, value]) => ({ name, value })))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Clickable Stat Card Component
  const StatCard = ({ title, value, icon: Icon, color, onClick, subtitle, trend }) => (
    <Card 
      className={cn(
        "bg-slate-800 border-slate-700 cursor-pointer transition-all hover:border-slate-500 hover:bg-slate-750",
        onClick && "hover:scale-[1.02]"
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className={cn("text-2xl font-bold", color || "text-white")}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={cn("p-3 rounded-lg", `bg-${color?.replace('text-', '')}/20` || "bg-slate-700")}>
            <Icon className={cn("h-6 w-6", color || "text-muted-foreground")} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-2">
            <TrendingUp className="h-3 w-3 text-green-400" />
            <span className="text-xs text-green-400">{trend}</span>
          </div>
        )}
        {onClick && (
          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <span>Click to view</span>
            <ChevronRight className="h-3 w-3" />
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Render Dashboard Section
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Clickable Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard 
          title="Total Tenants" 
          value={stats.total_tenants || 0} 
          icon={Building2} 
          color="text-blue-400"
          onClick={() => setActiveSection('tenants')}
          subtitle="All organizations"
        />
        <StatCard 
          title="Active" 
          value={stats.active_tenants || 0} 
          icon={CheckCircle} 
          color="text-green-400"
          onClick={() => { setStatusFilter('active'); setActiveSection('tenants'); }}
          subtitle="Paying customers"
        />
        <StatCard 
          title="Total Users" 
          value={stats.total_users || 0} 
          icon={Users} 
          color="text-purple-400"
          onClick={() => setActiveSection('users')}
          subtitle="Across all tenants"
        />
        <StatCard 
          title="MRR" 
          value={`$${stats.mrr || 0}`} 
          icon={DollarSign} 
          color="text-yellow-400"
          onClick={() => setActiveSection('billing')}
          subtitle="Monthly recurring"
        />
        <StatCard 
          title="Total Calls" 
          value={stats.total_calls || 0} 
          icon={Phone} 
          color="text-cyan-400"
          onClick={() => setActiveSection('tenants')}
          subtitle="Analyzed calls"
        />
        <StatCard 
          title="Trials Expiring" 
          value={stats.trials_expiring_soon || 0} 
          icon={Clock} 
          color="text-orange-400"
          onClick={() => { setStatusFilter('trialing'); setActiveSection('tenants'); }}
          subtitle="In next 7 days"
        />
      </div>

      {/* Quick Actions */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setAnnouncementDialog(true)} className="border-slate-600 text-muted-foreground/70">
              <Megaphone className="h-4 w-4 mr-2" /> Send Announcement
            </Button>
            <Button size="sm" variant="outline" onClick={() => setActiveSection('plans')} className="border-slate-600 text-muted-foreground/70">
              <Package className="h-4 w-4 mr-2" /> Manage Plans
            </Button>
            <Button size="sm" variant="outline" onClick={() => setActiveSection('api-keys')} className="border-slate-600 text-muted-foreground/70">
              <Key className="h-4 w-4 mr-2" /> View API Keys
            </Button>
            <Button size="sm" variant="outline" onClick={() => setActiveSection('audit')} className="border-slate-600 text-muted-foreground/70">
              <Activity className="h-4 w-4 mr-2" /> Audit Logs
            </Button>
            <Button size="sm" variant="outline" onClick={fetchDashboardData} className="border-slate-600 text-muted-foreground/70">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500" onClick={() => setActiveSection('subscriptions')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center justify-between">
              Subscription Status
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie data={subscriptionData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={5} dataKey="value">
                    {subscriptionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                </RePieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {subscriptionData.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                  <span className="text-muted-foreground">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500" onClick={() => setActiveSection('plans')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center justify-between">
              Plan Distribution
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={planData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} />
                  <YAxis stroke="#9ca3af" fontSize={10} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                  <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-sm flex items-center justify-between">
            Recent Activity
            <Button size="sm" variant="ghost" onClick={() => setActiveSection('audit')} className="text-muted-foreground">
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {dashboardData?.recent_activity?.slice(0, 5).map((log, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-white">{log.action}</p>
                    <p className="text-xs text-muted-foreground">{log.tenant_name || 'System'}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</span>
              </div>
            )) || <p className="text-muted-foreground text-center py-4">No recent activity</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Tenants Section
  const renderTenants = () => (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-white">All Tenants</CardTitle>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tenants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTenants()}
                className="pl-10 w-64 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setTimeout(fetchTenants, 100); }}>
              <SelectTrigger className="w-36 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
            <Button size="icon" variant="outline" onClick={fetchTenants} className="border-slate-600">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-muted-foreground">Company</TableHead>
              <TableHead className="text-muted-foreground">Plan</TableHead>
              <TableHead className="text-muted-foreground">Status</TableHead>
              <TableHead className="text-muted-foreground">Users</TableHead>
              <TableHead className="text-muted-foreground">Created</TableHead>
              <TableHead className="text-muted-foreground text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.map((tenant) => (
              <TableRow key={tenant.id} className="border-slate-700 hover:bg-slate-700/50 cursor-pointer" onClick={() => fetchTenantDetails(tenant.id)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${tenant.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                    <div>
                      <p className="font-medium text-white">{tenant.name}</p>
                      <p className="text-xs text-muted-foreground">{tenant.slug}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-slate-600 text-muted-foreground/70">
                    {plans.find(p => p.id === tenant.plan_id)?.name || 'Free'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className={statusColors[tenant.subscription_status] || 'bg-muted/500/20 text-muted-foreground'}>
                    {tenant.subscription_status}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground/70">{tenant.user_count || 0}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{new Date(tenant.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEnterTenant(tenant.id)} disabled={impersonating || !tenant.is_active} className="h-8 px-2 text-purple-400 hover:text-purple-300">
                      <LogIn className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => fetchTenantDetails(tenant.id)} className="h-8 px-2 text-muted-foreground hover:text-white">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                        <DropdownMenuItem onClick={() => fetchTenantDetails(tenant.id)} className="text-muted-foreground/70 cursor-pointer">
                          <Settings className="mr-2 h-4 w-4" /> Manage
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleSuspendTenant(tenant.id, tenant.is_active)} className={tenant.is_active ? 'text-yellow-400' : 'text-green-400'}>
                          {tenant.is_active ? <Ban className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
                          {tenant.is_active ? 'Suspend' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-slate-700" />
                        <DropdownMenuItem onClick={() => handleDeleteTenant(tenant.id)} className="text-red-400">
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  // Render All Users Section
  const renderUsers = () => (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white">All Users ({allUsers.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={fetchAllUsers} className="border-slate-600 text-muted-foreground/70">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-muted-foreground">User</TableHead>
              <TableHead className="text-muted-foreground">Tenant</TableHead>
              <TableHead className="text-muted-foreground">Role</TableHead>
              <TableHead className="text-muted-foreground">Status</TableHead>
              <TableHead className="text-muted-foreground">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allUsers.map((user) => (
              <TableRow key={user.id} className="border-slate-700">
                <TableCell>
                  <div>
                    <p className="font-medium text-white">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground/70">{user.tenant_name}</TableCell>
                <TableCell><Badge variant="outline" className="border-slate-600 text-muted-foreground/70">{user.role}</Badge></TableCell>
                <TableCell>
                  {user.is_active ? (
                    <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400">Suspended</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleImpersonateUser(user)} 
                      disabled={impersonating || !user.is_active}
                      className="h-8 px-2 text-purple-400 hover:text-purple-300"
                      title="Login as this user"
                    >
                      <LogIn className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleResetPassword(user.tenant_id, user.id)} className="text-muted-foreground">
                      <Key className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  // Render Feature Flags Section
  const renderFeatureFlags = () => (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Feature Flags ({featureFlags.length})</CardTitle>
              <CardDescription className="text-muted-foreground">
                Control feature rollouts across all tenants
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={fetchFeatureFlags} className="border-slate-600 text-muted-foreground/70">
                <RefreshCw className={cn("h-4 w-4 mr-2", featureFlagsLoading && "animate-spin")} /> Refresh
              </Button>
              <Button size="sm" onClick={() => setCreateFlagDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="h-4 w-4 mr-2" /> Create Flag
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {featureFlagsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : featureFlags.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Sliders className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No feature flags created yet</p>
              <p className="text-sm">Create your first feature flag to control feature rollouts</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-muted-foreground">Flag</TableHead>
                  <TableHead className="text-muted-foreground">Key</TableHead>
                  <TableHead className="text-muted-foreground text-center">Default</TableHead>
                  <TableHead className="text-muted-foreground text-center">Rollout %</TableHead>
                  <TableHead className="text-muted-foreground text-center">Overrides</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {featureFlags.map((flag) => (
                  <TableRow key={flag.id} className="border-slate-700">
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{flag.name}</p>
                        {flag.description && <p className="text-xs text-muted-foreground">{flag.description}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-slate-700 px-2 py-1 rounded text-cyan-400">{flag.key}</code>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={flag.default_enabled}
                        onCheckedChange={() => handleToggleFlag(flag)}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-blue-500/20 text-blue-400">{flag.rollout_percentage}%</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => fetchFlagOverrides(flag)}
                        className="text-muted-foreground hover:text-white"
                      >
                        <span className="text-sm">{flag.tenant_overrides_count || 0}</span>
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </TableCell>
                    <TableCell>
                      {flag.is_active ? (
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-500/20 text-gray-400">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem onClick={() => fetchFlagOverrides(flag)} className="text-muted-foreground/70 cursor-pointer">
                            <Settings className="mr-2 h-4 w-4" /> Manage Overrides
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteFlag(flag.id)} className="text-red-400 cursor-pointer">
                            <Trash2 className="mr-2 h-4 w-4" /> Delete Flag
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Flag Dialog */}
      <Dialog open={createFlagDialog} onOpenChange={setCreateFlagDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Create Feature Flag</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Add a new feature flag to control feature rollouts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Name</Label>
              <Input
                value={newFlag.name}
                onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })}
                placeholder="e.g., AI Coaching V2"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Key</Label>
              <Input
                value={newFlag.key}
                onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g., ai_coaching_v2"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-muted-foreground">Used in code to check if feature is enabled</p>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={newFlag.description}
                onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
                placeholder="What does this feature flag control?"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enabled by default</Label>
              <Switch
                checked={newFlag.default_enabled}
                onCheckedChange={(checked) => setNewFlag({ ...newFlag, default_enabled: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Rollout Percentage: {newFlag.rollout_percentage}%</Label>
              <Input
                type="range"
                min="0"
                max="100"
                value={newFlag.rollout_percentage}
                onChange={(e) => setNewFlag({ ...newFlag, rollout_percentage: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFlagDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleCreateFlag} className="bg-purple-600 hover:bg-purple-700">
              Create Flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flag Overrides Dialog */}
      <Dialog open={flagOverridesDialog} onOpenChange={setFlagOverridesDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Tenant Overrides: {selectedFlag?.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Override the default flag value for specific tenants
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <span className="text-white">Default Value</span>
              <Badge className={selectedFlag?.default_enabled ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}>
                {selectedFlag?.default_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Current Overrides</Label>
              {flagOverrides.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No tenant-specific overrides</p>
              ) : (
                <div className="space-y-2">
                  {flagOverrides.map((override) => (
                    <div key={override.tenant_id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-white">{override.tenant_name}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={override.enabled ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                          {override.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Button size="sm" variant="ghost" onClick={() => handleRemoveOverride(override.tenant_id)} className="text-red-400">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-white">Add Override</Label>
              <div className="flex gap-2">
                <Select onValueChange={(tenantId) => handleSetOverride(tenantId, !selectedFlag?.default_enabled)}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white flex-1">
                    <SelectValue placeholder="Select tenant to override" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {tenants
                      .filter(t => !flagOverrides.find(o => o.tenant_id === t.id))
                      .map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id} className="text-white">
                          {tenant.name}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Selecting a tenant will set its override to the opposite of the default ({selectedFlag?.default_enabled ? 'Disabled' : 'Enabled'})
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagOverridesDialog(false)} className="border-slate-600">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Render API Keys Section
  const renderApiKeys = () => (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white">All API Keys ({apiKeys.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={fetchAllApiKeys} className="border-slate-600 text-muted-foreground/70">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700">
              <TableHead className="text-muted-foreground">Name</TableHead>
              <TableHead className="text-muted-foreground">Tenant</TableHead>
              <TableHead className="text-muted-foreground">Key Prefix</TableHead>
              <TableHead className="text-muted-foreground">Scopes</TableHead>
              <TableHead className="text-muted-foreground">Status</TableHead>
              <TableHead className="text-muted-foreground">Last Used</TableHead>
              <TableHead className="text-muted-foreground">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map((key) => (
              <TableRow key={key.id} className="border-slate-700">
                <TableCell className="text-white">{key.name}</TableCell>
                <TableCell className="text-muted-foreground/70">{key.tenant_name}</TableCell>
                <TableCell className="text-muted-foreground font-mono text-xs">{key.key_prefix}...</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {key.scopes?.map(s => <Badge key={s} variant="outline" className="border-slate-600 text-xs">{s}</Badge>)}
                  </div>
                </TableCell>
                <TableCell>
                  {key.is_active ? (
                    <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400">Revoked</Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</TableCell>
                <TableCell>
                  {key.is_active && (
                    <Button size="sm" variant="ghost" onClick={() => handleRevokeApiKey(key.id)} className="text-red-400">
                      <Ban className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  // Render Plans Section
  const renderPlans = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Subscription Plans</h2>
        <Button onClick={() => setCreatePlanDialog(true)} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="h-4 w-4 mr-2" /> Create Plan
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <Card key={plan.id} className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">{plan.name}</CardTitle>
                {plan.slug === 'enterprise' && <Crown className="h-5 w-5 text-yellow-400" />}
              </div>
              <CardDescription className="text-muted-foreground">{plan.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">${plan.price_monthly_usd}</span>
                <span className="text-muted-foreground">/mo</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground/70">
                  <span>Users</span>
                  <span>{plan.limits?.max_users || '∞'}</span>
                </div>
                <div className="flex justify-between text-muted-foreground/70">
                  <span>Calls/month</span>
                  <span>{plan.limits?.max_calls_per_month || '∞'}</span>
                </div>
                <div className="flex justify-between text-muted-foreground/70">
                  <span>Storage</span>
                  <span>{plan.limits?.max_storage_gb || '∞'} GB</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-xs text-muted-foreground">
                  {tenants.filter(t => t.plan_id === plan.id).length} tenants on this plan
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Render System Health Section
  const renderSystemHealth = () => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'healthy': return 'text-green-400';
        case 'degraded': return 'text-yellow-400';
        case 'unhealthy': return 'text-red-400';
        default: return 'text-gray-400';
      }
    };

    const getStatusBg = (status) => {
      switch (status) {
        case 'healthy': return 'bg-green-500/20';
        case 'degraded': return 'bg-yellow-500/20';
        case 'unhealthy': return 'bg-red-500/20';
        default: return 'bg-gray-500/20';
      }
    };

    const getAlertColor = (type) => {
      switch (type) {
        case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
        case 'warning': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      }
    };

    return (
      <div className="space-y-6">
        {/* Overall Status Banner */}
        <Card className={cn("border-2", systemHealth?.status === 'healthy' ? 'border-green-500/30 bg-green-500/5' : systemHealth?.status === 'degraded' ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-red-500/30 bg-red-500/5')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {healthLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                ) : systemHealth?.status === 'healthy' ? (
                  <CheckCircle className="h-8 w-8 text-green-400" />
                ) : systemHealth?.status === 'degraded' ? (
                  <AlertTriangle className="h-8 w-8 text-yellow-400" />
                ) : (
                  <XCircle className="h-8 w-8 text-red-400" />
                )}
                <div>
                  <h3 className={cn("text-2xl font-bold capitalize", getStatusColor(systemHealth?.status))}>
                    System {systemHealth?.status || 'Unknown'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Last checked: {systemHealth?.timestamp ? new Date(systemHealth.timestamp).toLocaleString() : 'Never'}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => { fetchSystemHealth(); fetchProcessingStatus(); }} 
                variant="outline" 
                className="border-slate-600"
                disabled={healthLoading}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", healthLoading && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        {systemHealth?.alerts?.length > 0 && (
          <div className="space-y-2">
            {systemHealth.alerts.map((alert, idx) => (
              <div key={idx} className={cn("p-4 rounded-lg border", getAlertColor(alert.type))}>
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">{alert.message}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Component Health Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Database Health */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Database className="h-4 w-4" />
                Database
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusBg(systemHealth?.components?.database?.status)}>
                    {systemHealth?.components?.database?.status || 'Unknown'}
                  </Badge>
                </div>
                {systemHealth?.components?.database?.storage_used_mb && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Storage Used</span>
                    <span className="text-white text-sm">{systemHealth.components.database.storage_used_mb} MB</span>
                  </div>
                )}
                {systemHealth?.components?.database?.collections && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">Collections</span>
                    <span className="text-white text-sm">{systemHealth.components.database.collections}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Processing Queue Health */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Processing Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusBg(systemHealth?.components?.processing_queue?.status)}>
                    {systemHealth?.components?.processing_queue?.status || 'Unknown'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-slate-700/50 p-2 rounded text-center">
                    <p className="text-yellow-400 font-bold">{systemHealth?.components?.processing_queue?.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="bg-slate-700/50 p-2 rounded text-center">
                    <p className="text-blue-400 font-bold">{systemHealth?.components?.processing_queue?.processing || 0}</p>
                    <p className="text-xs text-muted-foreground">Processing</p>
                  </div>
                  <div className="bg-slate-700/50 p-2 rounded text-center">
                    <p className="text-red-400 font-bold">{systemHealth?.components?.processing_queue?.failed || 0}</p>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </div>
                  <div className="bg-slate-700/50 p-2 rounded text-center">
                    <p className="text-orange-400 font-bold">{systemHealth?.components?.processing_queue?.stuck || 0}</p>
                    <p className="text-xs text-muted-foreground">Stuck</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sessions Health */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Active Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusBg(systemHealth?.components?.sessions?.status)}>
                    {systemHealth?.components?.sessions?.status || 'Unknown'}
                  </Badge>
                </div>
                <div className="text-center p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-3xl font-bold text-green-400">{systemHealth?.components?.sessions?.active_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Users Online</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tenants Health */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Tenants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusBg(systemHealth?.components?.tenants?.status)}>
                    {systemHealth?.components?.tenants?.status || 'Unknown'}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-slate-700/50 p-2 rounded text-center">
                    <p className="text-blue-400 font-bold">{systemHealth?.components?.tenants?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div className="bg-slate-700/50 p-2 rounded text-center">
                    <p className="text-green-400 font-bold">{systemHealth?.components?.tenants?.active || 0}</p>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </div>
                  <div className="bg-slate-700/50 p-2 rounded text-center">
                    <p className="text-red-400 font-bold">{systemHealth?.components?.tenants?.suspended || 0}</p>
                    <p className="text-xs text-muted-foreground">Suspended</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Health */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Globe className="h-4 w-4" />
                API
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusBg(systemHealth?.components?.api?.status)}>
                    {systemHealth?.components?.api?.status || 'Unknown'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Errors (last hour)</span>
                  <span className={cn("font-bold", (systemHealth?.components?.api?.errors_last_hour || 0) > 0 ? "text-red-400" : "text-green-400")}>
                    {systemHealth?.components?.api?.errors_last_hour || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Processing Performance */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Processing Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {metricsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Avg Processing Time</span>
                    <span className="text-white font-medium">{systemMetrics?.processing_performance?.avg_seconds || 0}s</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Processed (24h)</span>
                    <span className="text-white font-medium">{systemMetrics?.processing_performance?.total_processed || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Active Tenants (24h)</span>
                    <span className="text-white font-medium">{systemMetrics?.active_tenants_in_period || 0}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Processing Status by Tenant */}
        {processingStatus?.tenant_breakdown?.length > 0 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-sm">Processing Queue by Tenant</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-muted-foreground">Tenant</TableHead>
                    <TableHead className="text-muted-foreground text-center">Pending</TableHead>
                    <TableHead className="text-muted-foreground text-center">Processing</TableHead>
                    <TableHead className="text-muted-foreground text-center">Completed</TableHead>
                    <TableHead className="text-muted-foreground text-center">Failed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processingStatus.tenant_breakdown.slice(0, 10).map((tenant) => (
                    <TableRow key={tenant.tenant_id} className="border-slate-700">
                      <TableCell className="text-white">{tenant.tenant_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-yellow-500/20 text-yellow-400">{tenant.pending}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-blue-500/20 text-blue-400">{tenant.processing}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-green-500/20 text-green-400">{tenant.completed}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-red-500/20 text-red-400">{tenant.failed}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Currently Processing */}
        {processingStatus?.currently_processing?.length > 0 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                Currently Processing ({processingStatus.currently_processing.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {processingStatus.currently_processing.map((call, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                      <span className="text-sm text-white truncate max-w-md">{call.filename}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Started: {call.started_at ? new Date(call.started_at).toLocaleTimeString() : 'Unknown'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render Audit Logs Section
  const renderAudit = () => {
    const getActionColor = (action) => {
      if (action?.includes('delete')) return 'text-red-400';
      if (action?.includes('create') || action?.includes('add')) return 'text-green-400';
      if (action?.includes('update') || action?.includes('edit')) return 'text-blue-400';
      if (action?.includes('login') || action?.includes('auth')) return 'text-purple-400';
      return 'text-muted-foreground';
    };

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        {auditStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Activity className="h-8 w-8 text-blue-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{auditLogsPagination.total}</p>
                    <p className="text-xs text-muted-foreground">Total Events</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Users className="h-8 w-8 text-green-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{auditStats.active_users}</p>
                    <p className="text-xs text-muted-foreground">Active Users (7d)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <BarChart3 className="h-8 w-8 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{auditFilterOptions.action_types?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Action Types</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Database className="h-8 w-8 text-orange-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{auditFilterOptions.resource_types?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Resource Types</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <Input
                placeholder="Search..."
                value={auditFilters.search}
                onChange={(e) => setAuditFilters({ ...auditFilters, search: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
              />
              <Select
                value={auditFilters.action_type}
                onValueChange={(v) => setAuditFilters({ ...auditFilters, action_type: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Action Type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Actions</SelectItem>
                  {auditFilterOptions.action_types?.slice(0, 15).map((a) => (
                    <SelectItem key={a.action} value={a.action} className="text-white">
                      {a.action} ({a.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={auditFilters.resource_type}
                onValueChange={(v) => setAuditFilters({ ...auditFilters, resource_type: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Resource Type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Resources</SelectItem>
                  {auditFilterOptions.resource_types?.map((r) => (
                    <SelectItem key={r.type} value={r.type} className="text-white">
                      {r.type} ({r.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={auditFilters.tenant_id}
                onValueChange={(v) => setAuditFilters({ ...auditFilters, tenant_id: v === 'all' ? '' : v })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Tenant" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => fetchDetailedAuditLogs(1)} className="bg-purple-600 hover:bg-purple-700">
                <Search className="h-4 w-4 mr-2" /> Filter
              </Button>
              <Button variant="outline" onClick={handleExportAuditLogs} className="border-slate-600">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Audit Logs ({auditLogsPagination.total})</CardTitle>
              <Button size="sm" variant="outline" onClick={() => fetchDetailedAuditLogs(auditLogsPagination.page)} className="border-slate-600">
                <RefreshCw className={cn("h-4 w-4 mr-2", auditLogsLoading && "animate-spin")} /> Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {auditLogsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-muted-foreground">Timestamp</TableHead>
                      <TableHead className="text-muted-foreground">Action</TableHead>
                      <TableHead className="text-muted-foreground">User</TableHead>
                      <TableHead className="text-muted-foreground">Tenant</TableHead>
                      <TableHead className="text-muted-foreground">Resource</TableHead>
                      <TableHead className="text-muted-foreground">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailedAuditLogs.map((log) => (
                      <TableRow key={log.id} className="border-slate-700">
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(log.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("bg-slate-700", getActionColor(log.action))}>
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="text-white">{log.user_name || 'System'}</p>
                            {log.user_email && <p className="text-xs text-muted-foreground">{log.user_email}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {log.tenant_name || '—'}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="text-white">{log.resource_type}</p>
                            {log.resource_id && <p className="text-xs text-muted-foreground truncate max-w-[150px]">{log.resource_id}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-muted-foreground">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="bg-slate-800 border-slate-700">
                                <DialogHeader>
                                  <DialogTitle className="text-white">Event Details</DialogTitle>
                                </DialogHeader>
                                <pre className="text-xs text-muted-foreground bg-slate-900 p-4 rounded-lg overflow-auto max-h-[300px]">
                                  {JSON.stringify(log.metadata, null, 2)}
                                </pre>
                              </DialogContent>
                            </Dialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {auditLogsPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {auditLogsPagination.page} of {auditLogsPagination.totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchDetailedAuditLogs(auditLogsPagination.page - 1)}
                        disabled={auditLogsPagination.page <= 1}
                        className="border-slate-600"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchDetailedAuditLogs(auditLogsPagination.page + 1)}
                        disabled={auditLogsPagination.page >= auditLogsPagination.totalPages}
                        className="border-slate-600"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Announcements Section
  const renderAnnouncements = () => {
    const getTypeColor = (type) => {
      switch (type) {
        case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
        case 'warning': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      }
    };

    const getTypeIcon = (type) => {
      switch (type) {
        case 'critical': return <AlertCircle className="h-5 w-5" />;
        case 'warning': return <AlertTriangle className="h-5 w-5" />;
        default: return <Info className="h-5 w-5" />;
      }
    };

    return (
      <div className="space-y-6">
        {/* Create Announcement Card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">System Announcements ({announcements.length})</CardTitle>
                <CardDescription className="text-muted-foreground">Broadcast messages to tenants</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={fetchAnnouncements} className="border-slate-600">
                  <RefreshCw className={cn("h-4 w-4 mr-2", announcementsLoading && "animate-spin")} /> Refresh
                </Button>
                <Button size="sm" onClick={() => setCreateAnnouncementDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" /> New Announcement
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {announcementsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No announcements yet</p>
                <p className="text-sm">Create your first announcement to broadcast to tenants</p>
              </div>
            ) : (
              <div className="space-y-4">
                {announcements.map((ann) => (
                  <div key={ann.id} className={cn("p-4 rounded-lg border", getTypeColor(ann.type), !ann.is_active && "opacity-50")}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getTypeIcon(ann.type)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-white">{ann.title}</h4>
                            <Badge variant="outline" className="text-xs">
                              {ann.target_type === 'all' ? 'All Tenants' : ann.target_type === 'specific_tenants' ? `${ann.target_ids?.length || 0} Tenants` : `${ann.target_ids?.length || 0} Plans`}
                            </Badge>
                            {!ann.is_active && <Badge className="bg-gray-500/20 text-gray-400">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ann.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Created: {new Date(ann.created_at).toLocaleDateString()}</span>
                            {ann.end_date && <span>Expires: {new Date(ann.end_date).toLocaleDateString()}</span>}
                            <span>{ann.dismissed_count || 0} dismissed</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={ann.is_active}
                          onCheckedChange={() => handleToggleAnnouncementActive(ann)}
                        />
                        <Button size="sm" variant="ghost" onClick={() => { setSelectedAnnouncement(ann); setEditAnnouncementDialog(true); }} className="text-muted-foreground">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteAnnouncement(ann.id)} className="text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Announcement Dialog */}
        <Dialog open={createAnnouncementDialog} onOpenChange={setCreateAnnouncementDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Create Announcement</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Broadcast a message to tenants. Supports markdown formatting.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Title</Label>
                <Input
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                  placeholder="Announcement title"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Message (Markdown supported)</Label>
                <Textarea
                  value={newAnnouncement.message}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                  placeholder="Write your announcement... **bold**, *italic*, - lists supported"
                  className="bg-slate-700 border-slate-600 text-white min-h-32"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Type</Label>
                  <Select value={newAnnouncement.type} onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, type: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="info" className="text-white"><div className="flex items-center gap-2"><Info className="h-4 w-4 text-blue-400" /> Info</div></SelectItem>
                      <SelectItem value="warning" className="text-white"><div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-yellow-400" /> Warning</div></SelectItem>
                      <SelectItem value="critical" className="text-white"><div className="flex items-center gap-2"><AlertCircle className="h-4 w-4 text-red-400" /> Critical</div></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Target</Label>
                  <Select value={newAnnouncement.target_type} onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, target_type: v, target_ids: [] })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all" className="text-white">All Tenants</SelectItem>
                      <SelectItem value="specific_tenants" className="text-white">Specific Tenants</SelectItem>
                      <SelectItem value="specific_plans" className="text-white">Specific Plans</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {newAnnouncement.target_type === 'specific_tenants' && (
                <div className="space-y-2">
                  <Label className="text-white">Select Tenants</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-700/50 rounded-lg max-h-32 overflow-y-auto">
                    {tenants.map((t) => (
                      <Badge
                        key={t.id}
                        variant={newAnnouncement.target_ids?.includes(t.id) ? "default" : "outline"}
                        className={cn("cursor-pointer", newAnnouncement.target_ids?.includes(t.id) ? "bg-purple-600" : "border-slate-500")}
                        onClick={() => {
                          const ids = newAnnouncement.target_ids || [];
                          setNewAnnouncement({
                            ...newAnnouncement,
                            target_ids: ids.includes(t.id) ? ids.filter(id => id !== t.id) : [...ids, t.id]
                          });
                        }}
                      >
                        {t.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label className="text-white">Allow users to dismiss</Label>
                <Switch
                  checked={newAnnouncement.is_dismissible}
                  onCheckedChange={(v) => setNewAnnouncement({ ...newAnnouncement, is_dismissible: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateAnnouncementDialog(false)} className="border-slate-600">Cancel</Button>
              <Button onClick={handleCreateAnnouncement} className="bg-purple-600 hover:bg-purple-700">Create Announcement</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Announcement Dialog */}
        <Dialog open={editAnnouncementDialog} onOpenChange={setEditAnnouncementDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Announcement</DialogTitle>
            </DialogHeader>
            {selectedAnnouncement && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Title</Label>
                  <Input
                    value={selectedAnnouncement.title}
                    onChange={(e) => setSelectedAnnouncement({ ...selectedAnnouncement, title: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Message</Label>
                  <Textarea
                    value={selectedAnnouncement.message}
                    onChange={(e) => setSelectedAnnouncement({ ...selectedAnnouncement, message: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white min-h-32"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Type</Label>
                    <Select value={selectedAnnouncement.type} onValueChange={(v) => setSelectedAnnouncement({ ...selectedAnnouncement, type: v })}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="info" className="text-white">Info</SelectItem>
                        <SelectItem value="warning" className="text-white">Warning</SelectItem>
                        <SelectItem value="critical" className="text-white">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Status</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Switch
                        checked={selectedAnnouncement.is_active}
                        onCheckedChange={(v) => setSelectedAnnouncement({ ...selectedAnnouncement, is_active: v })}
                      />
                      <span className="text-sm text-muted-foreground">{selectedAnnouncement.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditAnnouncementDialog(false)} className="border-slate-600">Cancel</Button>
              <Button onClick={handleUpdateAnnouncement} className="bg-purple-600 hover:bg-purple-700">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // Render Billing Section
  const renderBilling = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="MRR" value={`$${stats.mrr || 0}`} icon={DollarSign} color="text-green-400" subtitle="Monthly Recurring" />
        <StatCard title="ARR" value={`$${(stats.mrr || 0) * 12}`} icon={TrendingUp} color="text-blue-400" subtitle="Annual Recurring" />
        <StatCard title="Paid Tenants" value={tenants.filter(t => t.subscription_status === 'active').length} icon={CheckCircle} color="text-purple-400" />
        <StatCard title="Trials" value={tenants.filter(t => t.subscription_status === 'trialing').length} icon={Clock} color="text-orange-400" />
      </div>
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Revenue by Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={planData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
                <Bar dataKey="value" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render System Settings
  const renderSystem = () => (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">System Configuration</CardTitle>
          <CardDescription className="text-muted-foreground">Global platform settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Default Trial Days</Label>
              <Input type="number" defaultValue={14} className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-muted-foreground">Max API Keys per Tenant</Label>
              <Input type="number" defaultValue={5} className="bg-slate-700 border-slate-600 text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
            <div>
              <p className="text-white">Require Email Verification</p>
              <p className="text-xs text-muted-foreground">New users must verify email</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
            <div>
              <p className="text-white">Allow Self-Service Signup</p>
              <p className="text-xs text-muted-foreground">Anyone can create a tenant</p>
            </div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
            <div>
              <p className="text-white">Maintenance Mode</p>
              <p className="text-xs text-muted-foreground">Block all tenant access</p>
            </div>
            <Switch />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Main Render
  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <div className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Shield className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">Insyra AI</h1>
              <p className="text-xs text-purple-400">Super Admin</p>
            </div>
          </div>
        </div>
        <ScrollArea className="flex-1 p-4">
          <nav className="space-y-1">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  activeSection === item.id
                    ? "bg-purple-500/20 text-purple-400"
                    : "text-muted-foreground hover:text-white hover:bg-slate-700/50"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}
          </nav>
        </ScrollArea>
        <div className="p-4 border-t border-slate-700">
          <Button variant="ghost" onClick={logout} className="w-full justify-start text-muted-foreground hover:text-white">
            <X className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white capitalize">{activeSection.replace('-', ' ')}</h2>
            <p className="text-muted-foreground">
              {activeSection === 'dashboard' && 'Overview of your platform'}
              {activeSection === 'system-health' && 'Real-time system monitoring and health status'}
              {activeSection === 'tenants' && 'Manage all organizations'}
              {activeSection === 'users' && 'All users across tenants'}
              {activeSection === 'feature-flags' && 'Control feature rollouts across tenants'}
              {activeSection === 'subscriptions' && 'Subscription management'}
              {activeSection === 'billing' && 'Revenue and billing analytics'}
              {activeSection === 'api-keys' && 'API key management'}
              {activeSection === 'plans' && 'Subscription plans'}
              {activeSection === 'audit' && 'System activity logs'}
              {activeSection === 'announcements' && 'Send announcements to tenants'}
              {activeSection === 'system' && 'Platform configuration'}
              {activeSection === 'documentation' && 'Admin guides and docs'}
            </p>
          </div>

          {activeSection === 'dashboard' && renderDashboard()}
          {activeSection === 'system-health' && renderSystemHealth()}
          {activeSection === 'tenants' && renderTenants()}
          {activeSection === 'users' && renderUsers()}
          {activeSection === 'feature-flags' && renderFeatureFlags()}
          {activeSection === 'subscriptions' && renderTenants()}
          {activeSection === 'billing' && renderBilling()}
          {activeSection === 'api-keys' && renderApiKeys()}
          {activeSection === 'plans' && renderPlans()}
          {activeSection === 'audit' && renderAudit()}
          {activeSection === 'announcements' && renderAnnouncements()}
          {activeSection === 'documentation' && (
            <div className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    Platform Documentation
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">Admin guides and technical documentation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-slate-700/50 border-slate-600">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-white mb-2">🏢 Tenant Management</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground/70">
                          <li>• Create and configure tenants</li>
                          <li>• Set subscription plans and limits</li>
                          <li>• Manage user access and sessions</li>
                          <li>• Customize branding per tenant</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-700/50 border-slate-600">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-white mb-2">💳 Billing & Subscriptions</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground/70">
                          <li>• Monitor MRR and revenue</li>
                          <li>• Manage trial periods</li>
                          <li>• Process refunds and credits</li>
                          <li>• Configure subscription plans</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-700/50 border-slate-600">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-white mb-2">🔐 Security & Access</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground/70">
                          <li>• API key management</li>
                          <li>• Audit log monitoring</li>
                          <li>• Session control and termination</li>
                          <li>• Impersonation for support</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-700/50 border-slate-600">
                      <CardContent className="pt-4">
                        <h4 className="font-semibold text-white mb-2">⚙️ System Settings</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground/70">
                          <li>• Platform configuration</li>
                          <li>• Email templates and settings</li>
                          <li>• Feature flags management</li>
                          <li>• Maintenance mode control</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="pt-4 border-t border-slate-600">
                    <h4 className="font-semibold text-white mb-3">Quick Actions</h4>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" className="border-slate-600 text-muted-foreground/70 hover:text-white hover:bg-slate-700">
                        <Download className="h-4 w-4 mr-2" /> Export Admin Guide
                      </Button>
                      <Button variant="outline" className="border-slate-600 text-muted-foreground/70 hover:text-white hover:bg-slate-700">
                        <FileText className="h-4 w-4 mr-2" /> API Documentation
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          {activeSection === 'system' && renderSystem()}
        </div>
      </div>

      {/* Tenant Detail Sheet - Keep existing code */}
      <Sheet open={tenantDetailOpen} onOpenChange={setTenantDetailOpen}>
        <SheetContent className="w-full sm:max-w-2xl bg-slate-900 border-slate-700 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${selectedTenant?.tenant?.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
              {selectedTenant?.tenant?.name}
            </SheetTitle>
            <SheetDescription className="text-muted-foreground">
              {selectedTenant?.tenant?.slug} • Created {selectedTenant?.tenant?.created_at && new Date(selectedTenant.tenant.created_at).toLocaleDateString()}
            </SheetDescription>
          </SheetHeader>

          {selectedTenant && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
              <TabsList className="bg-slate-800 border-slate-700 flex-wrap">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="subscription">Subscription</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="sessions">Sessions</TabsTrigger>
                <TabsTrigger value="limits">Limits</TabsTrigger>
                <TabsTrigger value="branding">Branding</TabsTrigger>
                <TabsTrigger value="features">Features</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => handleEnterTenant(selectedTenant.tenant.id)} disabled={!selectedTenant.tenant.is_active} className="bg-purple-600 hover:bg-purple-700">
                    <LogIn className="h-4 w-4 mr-2" /> Enter Dashboard
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleSuspendTenant(selectedTenant.tenant.id, selectedTenant.tenant.is_active)} className={selectedTenant.tenant.is_active ? 'border-yellow-500/30 text-yellow-400' : 'border-green-500/30 text-green-400'}>
                    {selectedTenant.tenant.is_active ? <Ban className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                    {selectedTenant.tenant.is_active ? 'Suspend' : 'Activate'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={statusColors[selectedTenant.tenant.subscription_status]}>{selectedTenant.tenant.subscription_status}</Badge>
                  </div>
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Plan</p>
                    <p className="text-white font-medium">{selectedTenant.plan?.name || 'Free'}</p>
                  </div>
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Billing Email</p>
                    <p className="text-white text-sm">{selectedTenant.tenant.billing_email}</p>
                  </div>
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Trial Ends</p>
                    <p className="text-white text-sm">{selectedTenant.tenant.trial_ends_at ? new Date(selectedTenant.tenant.trial_ends_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
                <div className="p-4 bg-slate-800 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-white">Usage</p>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Users</span>
                        <span className="text-white">{selectedTenant.tenant.usage?.current_users || 0} / {selectedTenant.tenant.limits?.max_users || 0}</span>
                      </div>
                      <Progress value={(selectedTenant.tenant.usage?.current_users || 0) / (selectedTenant.tenant.limits?.max_users || 1) * 100} className="h-1.5" />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Calls</span>
                        <span className="text-white">{selectedTenant.tenant.usage?.calls_this_month || 0} / {selectedTenant.tenant.limits?.max_calls_per_month || 0}</span>
                      </div>
                      <Progress value={(selectedTenant.tenant.usage?.calls_this_month || 0) / (selectedTenant.tenant.limits?.max_calls_per_month || 1) * 100} className="h-1.5" />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Storage</span>
                        <span className="text-white">{(selectedTenant.tenant.usage?.storage_used_gb || 0).toFixed(2)} / {selectedTenant.tenant.limits?.max_storage_gb || 0} GB</span>
                      </div>
                      <Progress value={(selectedTenant.tenant.usage?.storage_used_gb || 0) / (selectedTenant.tenant.limits?.max_storage_gb || 1) * 100} className="h-1.5" />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="subscription" className="mt-4">
                <SubscriptionManager tenant={selectedTenant.tenant} plans={plans} authHeaders={getAuthHeaders()} onUpdate={() => fetchTenantDetails(selectedTenant.tenant.id)} />
              </TabsContent>

              <TabsContent value="users" className="mt-4">
                <div className="space-y-2">
                  {tenantUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-slate-600 text-muted-foreground/70">{user.role}</Badge>
                        {!user.is_active && <Badge className="bg-red-500/20 text-red-400">Suspended</Badge>}
                        <Button size="sm" variant="ghost" onClick={() => handleResetPassword(selectedTenant.tenant.id, user.id)} className="h-8 w-8 p-0">
                          <Key className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="sessions" className="mt-4">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-muted-foreground">{tenantSessions.filter(s => s.is_active).length} active sessions</p>
                  <Button size="sm" variant="outline" onClick={() => handleTerminateAllSessions(selectedTenant.tenant.id)} className="border-red-500/30 text-red-400">Terminate All</Button>
                </div>
                <div className="space-y-2">
                  {tenantSessions.map((session) => (
                    <div key={session.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                      <div>
                        <p className="text-white text-sm">{session.user?.email || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">Last active: {session.last_activity && new Date(session.last_activity).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {session.is_active ? <Badge className="bg-green-500/20 text-green-400">Active</Badge> : <Badge className="bg-muted/500/20 text-muted-foreground">Ended</Badge>}
                        {session.is_active && (
                          <Button size="sm" variant="ghost" onClick={() => handleTerminateSession(selectedTenant.tenant.id, session.id)} className="h-8 text-red-400">
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {tenantSessions.length === 0 && <p className="text-center text-muted-foreground py-8">No sessions found</p>}
                </div>
              </TabsContent>

              <TabsContent value="limits" className="mt-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Max Users</Label>
                      <Input type="number" value={editingLimits?.max_users ?? selectedTenant.tenant.limits?.max_users ?? 1} onChange={(e) => setEditingLimits({...editingLimits, max_users: parseInt(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Calls/Month</Label>
                      <Input type="number" value={editingLimits?.max_calls_per_month ?? selectedTenant.tenant.limits?.max_calls_per_month ?? 50} onChange={(e) => setEditingLimits({...editingLimits, max_calls_per_month: parseInt(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Storage (GB)</Label>
                      <Input type="number" step="0.1" value={editingLimits?.max_storage_gb ?? selectedTenant.tenant.limits?.max_storage_gb ?? 1} onChange={(e) => setEditingLimits({...editingLimits, max_storage_gb: parseFloat(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">API Rate/min</Label>
                      <Input type="number" value={editingLimits?.api_rate_limit ?? selectedTenant.tenant.limits?.api_rate_limit ?? 100} onChange={(e) => setEditingLimits({...editingLimits, api_rate_limit: parseInt(e.target.value)})} className="bg-slate-800 border-slate-600 text-white" />
                    </div>
                  </div>
                  {editingLimits && (
                    <div className="flex gap-2">
                      <Button onClick={handleUpdateLimits} className="bg-blue-600 hover:bg-blue-700">Save Limits</Button>
                      <Button variant="outline" onClick={() => setEditingLimits(null)} className="border-slate-600 text-muted-foreground/70">Cancel</Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="branding" className="mt-4">
                <div className="space-y-6">
                  <LogoUploader tenantId={selectedTenant.tenant.id} currentLogoUrl={selectedTenant.tenant.branding?.logo_url} authHeaders={getAuthHeaders()} onUpdate={() => fetchTenantDetails(selectedTenant.tenant.id)} />
                  <div className="border-t border-slate-700 pt-6">
                    <Label className="text-muted-foreground">Company Name</Label>
                    <Input value={editingBranding?.company_name ?? selectedTenant.tenant.branding?.company_name ?? ''} onChange={(e) => setEditingBranding({...editingBranding, company_name: e.target.value})} placeholder="Display name" className="bg-slate-800 border-slate-600 text-white" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Primary Color</Label>
                      <Input type="color" value={editingBranding?.primary_color ?? selectedTenant.tenant.branding?.primary_color ?? '#3b82f6'} onChange={(e) => setEditingBranding({...editingBranding, primary_color: e.target.value})} className="bg-slate-800 border-slate-600 h-10" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Secondary Color</Label>
                      <Input type="color" value={editingBranding?.secondary_color ?? selectedTenant.tenant.branding?.secondary_color ?? '#8b5cf6'} onChange={(e) => setEditingBranding({...editingBranding, secondary_color: e.target.value})} className="bg-slate-800 border-slate-600 h-10" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Accent Color</Label>
                      <Input type="color" value={editingBranding?.accent_color ?? selectedTenant.tenant.branding?.accent_color ?? '#22c55e'} onChange={(e) => setEditingBranding({...editingBranding, accent_color: e.target.value})} className="bg-slate-800 border-slate-600 h-10" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={editingBranding?.white_label ?? selectedTenant.tenant.branding?.white_label ?? false} onCheckedChange={(checked) => setEditingBranding({...editingBranding, white_label: checked})} />
                    <Label className="text-muted-foreground/70">White Label Mode</Label>
                  </div>
                  {editingBranding && (
                    <div className="flex gap-2">
                      <Button onClick={handleUpdateBranding} className="bg-blue-600 hover:bg-blue-700">Save Branding</Button>
                      <Button variant="outline" onClick={() => setEditingBranding(null)} className="border-slate-600 text-muted-foreground/70">Cancel</Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="features" className="mt-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-400" />
                      Feature Toggles
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      Enable or disable features for this organization
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* EEOC Training */}
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="text-white font-medium text-sm">EEOC Compliance Training</p>
                          <p className="text-xs text-muted-foreground">Voice-based interview compliance assessment</p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedTenant?.tenant?.features?.eeoc_enabled || false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.post(`/eeoc/super-admin/toggle-eeoc/${selectedTenant.tenant.id}?enabled=${checked}`, {}, { headers: getAuthHeaders() });
                            toast.success(`EEOC Training ${checked ? 'enabled' : 'disabled'}`);
                            fetchTenantDetails(selectedTenant.tenant.id);
                          } catch (error) {
                            toast.error('Failed to toggle EEOC feature');
                          }
                        }}
                      />
                    </div>
                    
                    {/* OneDrive Sync */}
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Upload className="h-5 w-5 text-cyan-400" />
                        <div>
                          <p className="text-white font-medium text-sm">OneDrive Sync</p>
                          <p className="text-xs text-muted-foreground">Auto-sync calls from OneDrive</p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedTenant?.tenant?.features?.onedrive_sync !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/features`, 
                              { onedrive_sync: checked }, 
                              { headers: getAuthHeaders() }
                            );
                            toast.success(`OneDrive Sync ${checked ? 'enabled' : 'disabled'}`);
                            fetchTenantDetails(selectedTenant.tenant.id);
                          } catch (error) {
                            toast.error('Failed to update feature');
                          }
                        }}
                      />
                    </div>
                    
                    {/* AI Call Analysis */}
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Zap className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="text-white font-medium text-sm">AI Call Analysis</p>
                          <p className="text-xs text-muted-foreground">Automatic call scoring & insights</p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedTenant?.tenant?.features?.ai_call_analysis !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/features`, 
                              { ai_call_analysis: checked }, 
                              { headers: getAuthHeaders() }
                            );
                            toast.success(`AI Analysis ${checked ? 'enabled' : 'disabled'}`);
                            fetchTenantDetails(selectedTenant.tenant.id);
                          } catch (error) {
                            toast.error('Failed to update feature');
                          }
                        }}
                      />
                    </div>
                    
                    {/* AI Coaching */}
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="text-white font-medium text-sm">AI Communication Coach</p>
                          <p className="text-xs text-muted-foreground">Personalized coaching suggestions</p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedTenant?.tenant?.features?.ai_coaching !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/features`, 
                              { ai_coaching: checked }, 
                              { headers: getAuthHeaders() }
                            );
                            toast.success(`AI Coaching ${checked ? 'enabled' : 'disabled'}`);
                            fetchTenantDetails(selectedTenant.tenant.id);
                          } catch (error) {
                            toast.error('Failed to update feature');
                          }
                        }}
                      />
                    </div>
                    
                    {/* API Access */}
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Key className="h-5 w-5 text-yellow-400" />
                        <div>
                          <p className="text-white font-medium text-sm">API Access</p>
                          <p className="text-xs text-muted-foreground">Enable API key generation</p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedTenant?.tenant?.features?.api_access !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/features`, 
                              { api_access: checked }, 
                              { headers: getAuthHeaders() }
                            );
                            toast.success(`API Access ${checked ? 'enabled' : 'disabled'}`);
                            fetchTenantDetails(selectedTenant.tenant.id);
                          } catch (error) {
                            toast.error('Failed to update feature');
                          }
                        }}
                      />
                    </div>
                    
                    {/* White Label */}
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Crown className="h-5 w-5 text-amber-400" />
                        <div>
                          <p className="text-white font-medium text-sm">White Label</p>
                          <p className="text-xs text-muted-foreground">Custom branding (Enterprise)</p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedTenant?.tenant?.features?.white_label || false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/features`, 
                              { white_label: checked }, 
                              { headers: getAuthHeaders() }
                            );
                            toast.success(`White Label ${checked ? 'enabled' : 'disabled'}`);
                            fetchTenantDetails(selectedTenant.tenant.id);
                          } catch (error) {
                            toast.error('Failed to update feature');
                          }
                        }}
                      />
                    </div>
                    
                    {/* Export */}
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Download className="h-5 w-5 text-teal-400" />
                        <div>
                          <p className="text-white font-medium text-sm">Data Export</p>
                          <p className="text-xs text-muted-foreground">Allow data export features</p>
                        </div>
                      </div>
                      <Switch
                        checked={selectedTenant?.tenant?.features?.export_enabled !== false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.put(`/super-admin/tenants/${selectedTenant.tenant.id}/features`, 
                              { export_enabled: checked }, 
                              { headers: getAuthHeaders() }
                            );
                            toast.success(`Export ${checked ? 'enabled' : 'disabled'}`);
                            fetchTenantDetails(selectedTenant.tenant.id);
                          } catch (error) {
                            toast.error('Failed to update feature');
                          }
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="activity" className="mt-4">
                <ScrollArea className="h-96">
                  <div className="space-y-2">
                    {tenantAuditLogs.map((log) => (
                      <div key={log.id} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="border-slate-600 text-muted-foreground/70 text-xs">{log.action}</Badge>
                          <span className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                    {tenantAuditLogs.length === 0 && <p className="text-center text-muted-foreground py-8">No activity logs</p>}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* Announcement Dialog */}
      <Dialog open={announcementDialog} onOpenChange={setAnnouncementDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Send Announcement</DialogTitle>
            <DialogDescription className="text-muted-foreground">This will be sent to all active tenants</DialogDescription>
          </DialogHeader>
          <Textarea value={announcementText} onChange={(e) => setAnnouncementText(e.target.value)} placeholder="Enter your announcement..." className="bg-slate-700 border-slate-600 text-white min-h-32" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnouncementDialog(false)} className="border-slate-600 text-muted-foreground/70">Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => { toast.success('Announcement sent!'); setAnnouncementDialog(false); setAnnouncementText(''); }}>
              <Send className="h-4 w-4 mr-2" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
